import { Link } from "react-router";

import { InitialsTile } from "../lib/InitialsTile.tsx";
import { Tag } from "../lib/Tag.tsx";
import { ACCESS_COLUMNS, ACCESS_COPY } from "./activity.constants.ts";
import type { AccessRow } from "./access.model.ts";

/**
 * The access trail, as a real table.
 *
 * Semantic markup for the reason `ActivityTable` gives — four fields per row,
 * the same four every time, and a screen reader announcing "User, Margit
 * Hauser" is the difference between a list and a log.
 *
 * THE "LOOKED AT" CELL IS SOMETIMES UNLINKED AND SOMETIMES UNNAMED, and this
 * component decides neither. `row.href` is null when the reader cannot open the
 * thing, and `row.lookedAt` already carries the server's refusal to name it —
 * both from `access.model.ts`, both ultimately from `ondo.access_trail()`. A
 * condition here about who may see what would be a second opinion on a
 * disclosure rule that lives in SQL.
 */
export function AccessTable({ rows, label }: { rows: AccessRow[]; label: string }) {
  return (
    <div className="activity__scroll">
      <table className="activity__table" aria-label={label}>
        <thead>
          <tr>
            {ACCESS_COLUMNS.map((column) => (
              <th key={column} scope="col" className="activity__head">
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td className="activity__cell">
                <span className="activity__who">
                  <InitialsTile
                    className="activity__initials"
                    initials={row.who.initials}
                  />
                  <span>
                    <span className="activity__name">{row.who.name}</span>
                    <span className="activity__ondo-id">{row.who.ondoId}</span>
                  </span>
                </span>
              </td>

              <td className="activity__cell activity__acting">{row.actingFor}</td>

              <td className="activity__cell">
                <span className="activity__glyph" aria-hidden="true">
                  {row.glyph}
                </span>
                {row.href ? (
                  <Link to={row.href} className="activity__action">
                    {row.lookedAt}
                  </Link>
                ) : (
                  <span className="activity__action">{row.lookedAt}</span>
                )}
                {row.code ? (
                  <span className="activity__subject">{row.code}</span>
                ) : null}
                <Tag tone={row.tag.tone}>{row.tag.label}</Tag>
              </td>

              <td className="activity__cell activity__when">
                {/* An unparseable stamp prints the em-dash rather than the raw
                    string: a trail that showed "2026-10-05T06:00:00.000Z" in a
                    When column would be showing its own plumbing. The row is
                    still here, which is the part that matters. */}
                {row.when ? (
                  <time dateTime={row.isoTime}>{row.when}</time>
                ) : (
                  ACCESS_COPY.asThemselves
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
